import React from "react";
import {makeStyles} from "@material-ui/core/styles";
import {
    Box,
    Card,
    FormControlLabel, Grid, Radio, RadioGroup,
    Typography,
    TextField, Link, Checkbox
} from "@material-ui/core";
import {colorInputBorder} from "../../Constants/theme";
import {sideEffects} from "../../Constants/prescriptionTypes";
import PrescriptionCardField from "./PrescriptionCardField";
import {IPrescription} from "../../interfaces";


const useStyles = makeStyles({
    card: {
        padding: "20px",
        border: `1px solid ${colorInputBorder}`,
        borderRadius: "6px",
        marginBottom: "15px"
    },
    formItem: {
        marginBottom: "1em",
    }
});

interface prescriptionCardProps {
    item: IPrescription,
    edit: () => void,
}

// eslint-disable-next-line react/prop-types
const PrescriptionCard: React.FC<prescriptionCardProps> = ({item, edit}) => {
    const classes = useStyles();
    const tabletFields = {
        TABLET: [
            {
                title: "Medication strength",
                value: item?.strength,
                measurementUnits: "MG",
            },
            {
                title: "Number of tablets daily",
                value: item?.numberOfTabletsDaily,
            },
            {
                title: "Number of days to give the medication",
                value: item?.numberOfDaysToGiveTheMedication,
            }
        ],
        LIQUID: [
            {
                title: "Medication concentration",
                value: item?.strength,
            },
            {
                title: "How many mL",
                value: item?.howManyMl,
                measurementUnits: "mL",
            },
            {
                title: "How many times given per day",
                value: item?.howManyTimesGivenPerDay,
            },
            {
                title: "Number of days to give the medication",
                value: item?.numberOfDaysToGiveTheMedication,
            }
        ],
        OTHER: [
            {
                title: "Medication strength",
                value: item?.strength,
            },
            {
                title: "Number of days to give the medication",
                value: item?.numberOfDaysToGiveTheMedication,
            }
        ]
    };

    return (
        <Card variant="outlined" className={classes.card}>
            <Grid container justify="flex-start" alignItems="center">
                <Grid item xs={12}>
                    <Box marginBottom="15px" display="flex" justifyContent="space-between">
                        <Typography variant="h4">Name of medication</Typography>
                        <Link
                            variant="body1"
                            style={{fontWeight: 700, cursor: "pointer", textDecoration: "none"}}
                            onClick={() => {
                                edit();
                            }}
                        >
                            Edit
                        </Link>
                    </Box>
                </Grid>
                <Grid item xs={12} className={classes.formItem}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        disabled
                        type="text"
                        value={item.name}
                        InputProps={{
                            style: {
                                color: "rgba(0, 0, 0, 0.5)",
                                fontStyle: "italic"
                            }
                        }}
                    />
                </Grid>
                {
                    item?.prescriptionType && tabletFields[item?.prescriptionType].map(({
                        title,
                        value,
                        measurementUnits
                    }, idx) => (
                        <PrescriptionCardField
                            key={idx}
                            title={title}
                            value={value}
                            measurementUnits={measurementUnits}
                        />
                    ))
                }
                <Grid item xs={12}>
                    <Box marginBottom="15px">
                        <Typography variant="h4">Quantity</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} className={classes.formItem}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        disabled
                        type="text"
                        value={item.quantity}
                        InputProps={{
                            style: {
                                color: "rgba(0, 0, 0, 0.5)",
                                fontStyle: "italic"
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box marginBottom="15px">
                        <Typography variant="h4">Refills</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} className={classes.formItem}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        disabled
                        type="text"
                        value={item.refills}
                        InputProps={{
                            style: {
                                color: "rgba(0, 0, 0, 0.5)",
                                fontStyle: "italic"
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box marginBottom="15px">
                        <Typography variant="h4">Label instructions</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} className={classes.formItem}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        disabled
                        type="text"
                        value={item.labelInstructions}
                        InputProps={{
                            style: {
                                color: "rgba(0, 0, 0, 0.5)",
                                fontStyle: "italic"
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box marginBottom="15px">
                        <Typography variant="h4">Other notes (optional)</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} className={classes.formItem}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        disabled
                        type="text"
                        value={item.notes || ""}
                        InputProps={{
                            style: {
                                color: "rgba(0, 0, 0, 0.5)",
                                fontStyle: "italic"
                            }
                        }}
                    />
                </Grid>
                <Grid container item xs={12} className={classes.formItem}>
                    <RadioGroup>
                        <Grid item xs="auto">
                            <FormControlLabel
                                control={<Checkbox/>}
                                color="secondary"
                                checked={item?.giveWithFoodAndWater}
                                label="Give with food and water"
                            />
                        </Grid>
                    </RadioGroup>
                </Grid>
                <Grid item xs={12}>
                    <Box marginBottom="15px">
                        <Typography variant="h4">Filling options</Typography>
                    </Box>
                </Grid>
                <Grid container item xs={12} className={classes.formItem}>
                    <RadioGroup>
                        <Grid item xs="auto">
                            <FormControlLabel
                                control={<Radio/>}
                                color="secondary"
                                checked={item.fillingOption === "RALPHIE_PHARMACY"}
                                label="The Video Vets pharmacy"
                            />
                        </Grid>
                        <Grid item xs="auto">
                            <FormControlLabel
                                control={<Radio/>}
                                color="secondary"
                                checked={item.fillingOption === "LOCAL_PHARMACY"}
                                label="Called into local (human) pharmacy"
                            />
                        </Grid>
                    </RadioGroup>
                </Grid>
                {item?.potentialSideEffects &&
                <>
                    <Grid item xs={12}>
                        <Box marginBottom="15px">
                            <Typography variant="h4">Potential Side Effects</Typography>
                        </Box>
                    </Grid>
                    <Grid container item xs={12} className={classes.formItem}>
                        <RadioGroup
                            row={false}
                        >
                            {
                                sideEffects.map(({label, value}, idx) => (
                                    <Grid item xs="auto" key={idx}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={item?.potentialSideEffects.includes(value)}
                                                    name={value}
                                                />
                                            }
                                            label={label}
                                        />
                                    </Grid>
                                ))
                            }
                        </RadioGroup>
                    </Grid>
                </>
                }
            </Grid>
        </Card>
    );
};

export default PrescriptionCard;
